import { useState } from 'react';
import type { ReactElement } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router';
import { ToastProvider } from '@xdocs/ui';
import { AuthProvider } from './auth/AuthProvider.js';
import { RequireAuth } from './auth/RequireAuth.js';
import { ImpersonationBanner, ImpersonationProvider } from './features/admin/index.js';
import { LocaleController } from './i18n/LocaleController.js';
import { createAppQueryClient } from './lib/queryClient.js';
import { AdminLayout, AnalyticsPage, OverviewPage, UserDetailPage, UsersListPage } from './routes/admin/index.js';
import { AssetsPage } from './routes/admin/assets/index.js';
import { DocumentsPage, DocumentDetailPage } from './routes/documents/index.js';
import { GeneratePage } from './routes/generate/index.js';
import { SearchPage } from './routes/search/index.js';
import { AuditPage } from './routes/admin/audit/index.js';
import { BillingPage as AdminBillingPage } from './routes/admin/billing/index.js';
import { CmsPage } from './routes/admin/cms/index.js';
import { InboxPage } from './routes/admin/inbox/index.js';
import { PromosPage } from './routes/admin/promos/index.js';
import { ActivityPage } from './routes/activity/index.js';
import { CheckoutErrorPage, CheckoutSuccessPage } from './routes/checkout/index.js';
import { DiscoverPage, DiscoverSharePage } from './routes/discover/index.js';
import { EditorPage } from './routes/edit/index.js';
import { ExplorerPage } from './routes/explorer/index.js';
import { FtueProvider, FtueTour } from './features/ftue/index.js';
import { LoginPage } from './routes/LoginPage.js';
import { NotFoundPage } from './routes/NotFoundPage.js';
import { NotificationsPage } from './routes/notifications/index.js';
import { DeployUpdateToast } from './pwa/DeployUpdateToast.js';
import { PersonasPage } from './routes/personas/index.js';
import { CreditsExplainerPage } from './routes/pricing/credits/index.js';
import { ReadPage } from './routes/read/index.js';
import { ResetPasswordPage } from './routes/ResetPasswordPage.js';
import { AuthCallbackPage } from './routes/AuthCallbackPage.js';
import { SettingsPage } from './routes/settings/index.js';
import { BillingPage } from './routes/settings/billing/index.js';
import { DriveSettingsPage } from './routes/settings/drive/index.js';
import { NotificationsSettingsPage } from './routes/settings/notifications/index.js';
import { ReferralsSettingsPage } from './routes/settings/referrals/index.js';
import { SessionsPage } from './routes/settings/sessions/index.js';
import { LinkResolvePage, SharesPage } from './routes/shares/index.js';
import { SignupPage } from './routes/SignupPage.js';
import { StylePage } from './routes/style/index.js';
import { TemplatesPage } from './routes/templates/index.js';
import { StyleguideRoute } from './routes/StyleguideRoute.js';
import { AppShellLayout } from './shell/AppShellLayout.js';
import { ThemeController } from './theme/ThemeController.js';
import { WsProvider } from './ws/WsProvider.js';

/** Root component: provider stack (outer → inner: locale/theme → toasts →
 * query cache → auth session → the one WS client → router) then routes
 * (task spec: `/login`, `/signup`, `/styleguide`, `/` authenticated shell,
 * 404 catch-all). */
export function App(): ReactElement {
  const [queryClient] = useState(() => createAppQueryClient());

  return (
    <LocaleController>
      <ThemeController>
        <ToastProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <WsProvider>
                {/* Mounted ABOVE the router (not inside AdminLayout): a
                    view-as session is entered from /admin/users/:userId but
                    the whole point is staff navigate OUT into the ordinary
                    app as the target user — the banner/provider must survive
                    that navigation (features/admin/WIRING.md §2). Renders
                    null when there's no active session — a no-op for every
                    non-staff user. */}
                <ImpersonationProvider>
                  <ImpersonationBanner />
                  {/* E9 update toast — a pure controller (renders nothing
                      itself), safe to mount unconditionally alongside every
                      route (pwa/WIRING.md §2). The E20 offline-queue chip is
                      a visual element instead — mounted in
                      `AppShellLayout`'s topbar, next to `ConnectionBanner`,
                      per that same doc's "wherever makes sense in the
                      header/nav" guidance. */}
                  <DeployUpdateToast />
                  {/* F9 FTUE — single shared `useFtue()` instance so the
                      root-mounted tour and the /settings/notifications
                      "Replay tutorial" button observe the same state
                      (features/ftue/WIRING.md §1). */}
                  <FtueProvider>
                    <FtueTour />
                    <BrowserRouter>
                      <Routes>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/signup" element={<SignupPage />} />
                      <Route path="/reset" element={<ResetPasswordPage />} />
                      <Route path="/auth/callback" element={<AuthCallbackPage />} />
                      <Route path="/styleguide" element={<StyleguideRoute />} />
                      {/* Public/anonymous-accessible surfaces (F62/F63 AC: "no
                          account needed") — deliberately OUTSIDE <RequireAuth>,
                          which would otherwise bounce a logged-out visitor to
                          /login and defeat the feature
                          (apps/web/src/features/sharing/WIRING.md §1). */}
                      <Route path="/discover/:shareId" element={<DiscoverSharePage />} />
                      <Route path="/s/:token" element={<LinkResolvePage />} />
                      {/* F48 credits explainer (design/app/33-credits-info.html) —
                          PUBLIC (user 2026-07-04: "shouldn't be auth gated"):
                          the cost table is marketing-adjacent; the page swaps
                          the checkout grid for a login CTA when logged out.
                          Destination of GeneratePage's insufficient-credits
                          "Buy more credits" CTA (§0.3). */}
                      <Route path="/pricing/credits" element={<CreditsExplainerPage />} />
                      <Route
                        element={
                          <RequireAuth>
                            <Outlet />
                          </RequireAuth>
                        }
                      >
                        <Route element={<AppShellLayout />}>
                          {/* The authenticated app's home IS the documents workspace
                              (design/app/18-documents.html) — `/documents` stays mounted
                              as an alias route to the same component so existing deep
                              links/bookmarks to `/documents` keep working. */}
                          <Route index element={<DocumentsPage />} />
                          <Route path="/documents" element={<DocumentsPage />} />
                          <Route path="/documents/:id" element={<DocumentDetailPage />} />
                          <Route path="/generate" element={<GeneratePage />} />
                          <Route path="/templates" element={<TemplatesPage />} />
                          <Route path="/search" element={<SearchPage />} />
                          <Route path="/explorer" element={<ExplorerPage />} />
                          <Route path="/explorer/preview/:fileId" element={<ExplorerPage />} />
                          <Route path="/discover" element={<DiscoverPage />} />
                          <Route path="/shares" element={<SharesPage />} />
                          {/* Dedicated notifications feed (design/app/32-notifications.html)
                              — the topbar bell (`NotificationsBell`, shared shell chrome)
                              is a quick-glance preview of the same data; this is the
                              "see everything" destination it and any other future
                              in-app link should point at. */}
                          <Route path="/notifications" element={<NotificationsPage />} />
                          <Route path="/edit/:docId" element={<EditorPage />} />
                          <Route path="/style/:docId" element={<StylePage />} />
                          <Route path="/settings" element={<SettingsPage />} />
                          <Route path="/settings/sessions" element={<SessionsPage />} />
                          <Route path="/settings/billing" element={<BillingPage />} />
                          <Route path="/settings/drive" element={<DriveSettingsPage />} />
                          <Route path="/settings/notifications" element={<NotificationsSettingsPage />} />
                          <Route path="/settings/referrals" element={<ReferralsSettingsPage />} />
                          <Route path="/personas" element={<PersonasPage />} />
                          <Route path="/activity" element={<ActivityPage />} />
                          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                          <Route path="/checkout/error" element={<CheckoutErrorPage />} />
                        </Route>
                        {/* Distraction-free e-reader (F66, design/app/30-reader.html)
                            — chromeless full-bleed view (its own Close/chapter-progress/
                            theme-swatch toolbar via <ReaderToolbar>), a SIBLING of
                            AppShellLayout rather than nested inside it so the sidebar/
                            topbar chrome never leaks in (still behind <RequireAuth>). */}
                        <Route path="/read/:docId" element={<ReadPage />} />
                        {/* Admin shell (F53-F61/F68/F70/F71) — its own
                            top-level sidebar+topbar (design/app/38-49), a
                            SIBLING of AppShellLayout, not nested inside it
                            (features/admin/WIRING.md §1). AdminLayout itself
                            gates at minimum='moderator'; admin-only pages
                            additionally self-wrap in <RoleGate minimum="admin">. */}
                        <Route element={<AdminLayout />}>
                          <Route path="/admin/overview" element={<OverviewPage />} />
                          <Route path="/admin/users" element={<UsersListPage />} />
                          <Route path="/admin/users/:userId" element={<UserDetailPage />} />
                          <Route path="/admin/analytics" element={<AnalyticsPage />} />
                          <Route path="/admin/billing" element={<AdminBillingPage />} />
                          <Route path="/admin/promos" element={<PromosPage />} />
                          <Route path="/admin/inbox" element={<InboxPage />} />
                          <Route path="/admin/assets" element={<AssetsPage />} />
                          <Route path="/admin/audit" element={<AuditPage />} />
                          <Route path="/admin/landing-cms" element={<CmsPage />} />
                        </Route>
                      </Route>
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                    </BrowserRouter>
                  </FtueProvider>
                </ImpersonationProvider>
              </WsProvider>
            </AuthProvider>
          </QueryClientProvider>
        </ToastProvider>
      </ThemeController>
    </LocaleController>
  );
}
